import * as Yup from "yup";
import { EMAIL_REGEX, TeamMemberCommitionRole } from "../../helpers/utils";
import { fieldIsRequired } from "./schemaMsg";
import dayjs from "dayjs";
// Define the objectIdSchema validation
export const objectIdSchema = (
  key: string,
  label: string,
  isRequired = false,
) => {
  let schema = Yup.string()
    .matches(/^[0-9a-fA-F]{24}$/, "Invalid object ID")
    .label(label);
  if (isRequired) {
    schema = schema.required();
  }
  return schema;
};

// Define the stringSchema validation
export const stringSchema = (
  key: string,
  label: string,
  isRequired = false,
) => {
  let schema = Yup.string().min(1).label(label);
  if (isRequired) {
    schema = schema.required(fieldIsRequired(label));
  }
  return schema;
};

export const EmailValidationSchema = {
  email: Yup.string()
    .matches(EMAIL_REGEX, "Please enter valid email")
    .required("Please enter email"),
};
//common email schema

export const emailSchema = (key: string, label: string, isRequired = false) => {
  let schema = Yup.string()
    .matches(EMAIL_REGEX, "Please enter valid email")
    .label(label);
  if (isRequired) {
    schema = schema.required(fieldIsRequired(label));
  }
  return schema;
};

export const addSubAdminSchema = Yup.object().shape({
  ...EmailValidationSchema,
  name: Yup.string().trim().required("Please enter name"),
  phone: Yup.string().required("Please enter phone number"),
});

export const editUserSchema = Yup.object().shape({
  ...EmailValidationSchema,
  name: Yup.string().trim().required("Please enter name"),
});

export const createHolidaySchema = Yup.object().shape({
  holidays: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().trim().required("Annual leave name is required"),
      date: Yup.mixed().required("Date is required"),
      type: Yup.string().oneOf(["paid", "unpaid"], "Invalid type").required("Type is required"),
    })
  ).min(1, "At least one annual leave is required"),
});

export const createMachineSchema = Yup.object().shape({
  name: Yup.string().trim().required("Machine is required"),

  department: Yup.array()
    .of(Yup.string().required())
    .min(1, "At least one department is required")
    .required("Department is required"),
});

export const createQualityChecklistSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .required("Enter checklist title")
    .min(1)
    .max(200),
  department: Yup.string()
    .required("Department is required"),
});

export const createDispatchChecklistSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .required("Enter checklist title")
    .min(1)
    .max(200),
  department: Yup.string()
    .required("Department is required"),
});

export const createQualityCheckQuestionSchema = Yup.object().shape({
  question_text: Yup.string()
    .trim()
    .required("Enter question text")
    .min(1)
    .max(1000),
  ans_type: Yup.string().required("Select answer type"),
  if_yes_action: Yup.string().when("ans_type", {
    is: (val: string) => val === "YES_NO",
    then: (schema) => schema.required("Select logic for Yes"),
    otherwise: (schema) => schema.notRequired(),
  }),
  if_no_action: Yup.string().when("ans_type", {
    is: (val: string) => val === "YES_NO",
    then: (schema) => schema.required("Select logic for No"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const createDispatchChecklistQuestionSchema = Yup.object().shape({
  question_text: Yup.string()
    .trim()
    .required("Enter question text")
    .min(1)
    .max(1000),
  ans_type: Yup.string().required("Select answer type"),
  if_yes_action: Yup.string().when("ans_type", {
    is: (val: string) => val === "YES_NO",
    then: (schema) => schema.required("Select logic for Yes"),
    otherwise: (schema) => schema.notRequired(),
  }),
  if_no_action: Yup.string().when("ans_type", {
    is: (val: string) => val === "YES_NO",
    then: (schema) => schema.required("Select logic for No"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const createReadinessCheckQuestionSchema = Yup.object().shape({
  question_text: Yup.string()
    .trim()
    .required("Enter question text")
    .min(1)
    .max(1000),
  ans_type: Yup.string().required("Select answer type"),
});
export const createReadinessSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .required("Enter checklist title")
    .min(1)
    .max(200),
  description: Yup.string()
    .trim()
    .max(2000),
});
export const createDepartmentSchema = Yup.object().shape({
  name: Yup.string().trim().required("Please enter department name"),
});
export const changeOrderIdSchema = Yup.object().shape({
  order_id: Yup.string().trim().required("Please enter order id"),
});
export const createHiredCompanySchema = Yup.object().shape({
  name: Yup.string().trim().required("Please enter company name"),
});
export const dispatchFormValidation = Yup.object().shape({
  dispatch_date: Yup.date().nullable().required("Dispatch date is required"),
  tracing_number: Yup.string().trim().required("Tracking number is required"),
  transporter: Yup.string().trim().required("Transporter is required"),
  vehicle_info: Yup.string().trim().required("Vehicle info is required"),
  // dispatch_note: Yup.string().trim().required("Dispatch note is required"),
});
export const salesCommissionSchema = Yup.object().shape({
  sales_person: Yup.string().nullable().required("Sales person is required"),

  commission_percentage: Yup.number()
    .typeError("Commission percentage must be a number")
    .required("Commission percentage is required")
    .min(0, "Commission cannot be less than 0")
    .max(100, "Commission cannot exceed 100"),
});

export const createTeamMemberSchema = Yup.object().shape({
  professional_title: Yup.string().nullable(),
  first_name: Yup.string().trim().required("Please enter first name"),
  last_name: Yup.string().trim().required("Please enter last name"),
  gender: Yup.string().nullable(),
  phone: Yup.string()
    .matches(/^[0-9+\-\s()]*$/, "Only numbers and symbols (+ - ) are allowed")
    .test("phone-too-short", "Phone number is too short", (value) => {
      if (!value) return true;
      const digits = value.replace(/\D/g, "");
      return digits.length >= 7;
    })
    .test("phone-too-long", "Phone number is too long", (value) => {
      if (!value) return true;
      const digits = value.replace(/\D/g, "");
      return digits.length <= 15;
    }),
  email: Yup.string()
    .trim()
    .matches(EMAIL_REGEX, "Please enter valid email")
    .required("Please enter email"),
  role: Yup.array()
    .of(Yup.string().required())
    .min(1, "Please select role")
    .max(3, "Maximum 3 roles can be selected")
    .required("Please select role"),
  department: Yup.array()
    .of(Yup.string().required())
    .min(1, "Please select department")
    .required("Please select department"),
  isEmployee: Yup.boolean().optional(),
  isApprover: Yup.boolean().optional(),

  // commission: Yup.number()
  //   .transform((value, originalValue) =>
  //     originalValue === "" ? undefined : value,
  //   )
  //   .when("roleKey", {
  //     is: (roleKey: string) => TeamMemberCommitionRole?.includes(roleKey),
  //     then: (schema) =>
  //       schema
  //         .typeError("Commission must be a number")
  //         .min(0, "Commission cannot be negative")
  //         .max(100, "Commission cannot exceed 100")
  //         .required("Please enter commission"),
  //     otherwise: (schema) => schema.notRequired(),
  //   }),
});

export const InstallationPlanSchema = Yup.object().shape({
  plant_hire_company: Yup.array()
    .min(1, "Plant hire company is required")
    .required("Plant hire company is required"),

  employee_ids: Yup.array()
    .min(1, "Installation manager is required")
    .required("Installation manager is required"),

  status: Yup.string()
    .required("Status is required")
    .notOneOf([""], "Status is required"),

  start_date: Yup.date().nullable().required("Start date is required"),

  end_date: Yup.date()
    .nullable()
    .required("End date is required")
    .test(
      "end-after-start",
      "End date must be greater than start date",
      function (value) {
        const { start_date } = this.parent;
        if (!start_date || !value) return true;
        return !dayjs(start_date).isAfter(dayjs(value));
      },
    ),
});
export const dispatchLoadValidation = Yup.object().shape({
  number_of_loads: Yup.number()
    .typeError("Number of Load / Number of Vehicle must be a number")
    .required("Number of Load / Number of Vehicle is required")
    .moreThan(0, "Number of Load / Number of Vehicle must be greater than 0")
    .max(
      50,
      "Number of Load / Number of Vehicle must be less than or equal to 50",
    ),
});

export const createInvoiceSchema = Yup.object().shape({
  order_id: Yup.string().trim().required("Please select an Order"),
  milestone_id: Yup.string().trim().required("Please select a Milestone"),
});

export const editInvoiceSchema = Yup.object().shape({
  invoice_number: Yup.string().trim().required("Please enter invoice number"),
  invoice_title: Yup.string().trim().required("Please enter invoice title"),
  
});
